import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Tooltip,
  styled,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import http from "../../utils/http";
import { timezoneLabel } from "../../utils/timezone";
import { Calendar, View, luxonLocalizer } from "react-big-calendar";
import { DateTime } from "luxon";
import {
  ChevronLeft,
  ChevronRight,
  Add,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import "../../appointments.scss";
import { errorToastMessage } from "../../utils/toast";
import { getCalendarParams } from "../../utils/appointments";
import AddAppointmentModal from "./AddAppointmentModal";
// import DeleteSlotModal from "./DeleteSlotModal";
import ViewAppointmentModal from "./ViewAppointmentModal";
import RescheduleAppointmentModal from "./RescheduleAppointmentModal";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import {
  addOrRemoveId,
  setSelectedIds,
  setSelectedSite,
  setSelectedRoom,
} from "../../Redux/reducers/appointmentSlice";
import type { Site, Slot } from "../../Redux/reducers/appointmentSlice";
import { mapSlotInviteesToDetails } from "../Appointments/appointmentParticipantUtils";

const localizer = luxonLocalizer(DateTime, { firstDayOfWeek: 1 });

// Custom styled tooltip
const CustomTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "#ffffff",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
    fontSize: 13,
    borderRadius: "8px",
    padding: "12px 16px",
    maxWidth: 320,
    border: "1px solid #E5E7EB",
  },
  "& .MuiTooltip-arrow": {
    color: "#ffffff",
    "&::before": {
      border: "1px solid #E5E7EB",
    },
  },
}));

// Empty slot tooltip component
const EmptySlotTooltip = ({
  time,
  isPastAppointment,
}: {
  time: string;
  isPastAppointment: boolean;
}) => (
  <Box>
    <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
      Available Time Slot
    </Typography>
    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
      {time}
    </Typography>
    {!isPastAppointment && (
      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
        Click + to schedule an appointment
      </Typography>
    )}
  </Box>
);

// Appointment tooltip component
const AppointmentTooltip = ({
  time,
  participants,
  inviters,
  study,
  notes,
  cohortName,
}: {
  time: string;
  participants: string;
  inviters: string;
  study: string;
  notes: string;
  cohortName?: string;
}) => (
  <Box>
    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: "#872EE5" }}>
      Appointment Details
    </Typography>

    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}>
        Time
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {time}
      </Typography>
    </Box>

    {participants && (
      <Box sx={{ mb: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}
        >
          Participants
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {participants}
        </Typography>
      </Box>
    )}

    {inviters && (
      <Box sx={{ mb: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}
        >
          Inviters
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {inviters}
        </Typography>
      </Box>
    )}

    {study && (
      <Box sx={{ mb: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}
        >
          Study
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {study}
        </Typography>
      </Box>
    )}

    {cohortName && (
      <Box sx={{ mb: 1 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}
        >
          User Group
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {cohortName}
        </Typography>
      </Box>
    )}

    {notes && (
      <Box>
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: "text.primary" }}
        >
          Notes
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {notes}
        </Typography>
      </Box>
    )}

    <Typography
      sx={{
        fontSize: 11,
        color: "text.secondary",
        mt: 1,
        fontStyle: "italic",
      }}
    >
      Click to view full details
    </Typography>
  </Box>
);

// Determine which icon to show based on status
const StatusIcon = ({ status }: { status: string }) => {
  if (status === "completed") {
    return (
      <CheckCircle
        sx={{
          fontSize: "14px",
          color: "inherit",
          flexShrink: 0,
        }}
      />
    );
  } else if (status === "no_show") {
    return (
      <Cancel
        sx={{
          fontSize: "14px",
          color: "inherit",
          flexShrink: 0,
        }}
      />
    );
  }
  return null;
};

// Custom Event Component
const CustomEvent = ({
  event,
  view,
  onAddClick,
}: // onDeleteSlotClick,
{
  event: Slot;
  view: View;
  onAddClick: (event: Slot) => void;
  // onDeleteSlotClick: (event: Slot) => void;
}) => {
  const appointment = event.appointment;

  // Check if appointment is in the past
  const isPastAppointment = useMemo(() => {
    if (!event?.startDateTime) return false;
    const appointmentDate = DateTime.fromJSDate(event.startDateTime);
    const now = DateTime.now();
    return appointmentDate < now;
  }, [event]);

  if (!appointment) {
    return (
      <CustomTooltip
        title={
          <EmptySlotTooltip
            time={event.time}
            isPastAppointment={isPastAppointment}
          />
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: view === "day" ? "flex-start" : "space-between",
            height: "100%",
            px: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {event.time}
          </Typography>
          {!isPastAppointment && (
            <IconButton
              size="small"
              sx={{
                p: 0,
                minWidth: "20px",
                minHeight: "20px",
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onAddClick(event);
              }}
            >
              <Add />
            </IconButton>
          )}
          {/* <IconButton
            size="small"
            sx={{
              p: 0,
              minWidth: "20px",
              minHeight: "20px",
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSlotClick(event);
            }}
          >
            <Delete />
          </IconButton> */}
        </Box>
      </CustomTooltip>
    );
  }

  // Appointment exists - show compact card
  const participantNames = appointment.invitees
    .map((p: any) => p.name)
    .join(", ");
  const inviters =
    appointment.inviters?.map((p: any) => p.name).join(", ") || "";
  const studyName = appointment?.study;
  const status = appointment?.status;

  // Extract unique cohort names from invitees
  const cohortMap = new Map<string, string>();
  appointment.invitees?.forEach((invitee: any) => {
    const cohortId = invitee?.cohortId;
    const cohortName = invitee?.cohortName;
    if (cohortId && cohortName && !cohortMap.has(cohortId)) {
      cohortMap.set(cohortId, cohortName);
    }
  });
  const uniqueCohortNames = Array.from(cohortMap.values());
  const cohortName =
    uniqueCohortNames.length > 0 ? uniqueCohortNames.join(", ") : "";

  // Build display text based on view
  const getDisplayText = () => {
    if (view === "month") {
      // Month view: time : study name : cohort name
      const parts = [event.time, studyName, cohortName].filter(Boolean);
      return parts.join(" : ");
    } else {
      // Week/Day views: study name : cohort name
      const parts = [studyName, cohortName].filter(Boolean);
      return parts.join(" : ");
    }
  };

  return (
    <CustomTooltip
      title={
        <AppointmentTooltip
          time={event.time}
          participants={participantNames}
          inviters={inviters}
          study={studyName}
          notes={appointment.notes}
          cohortName={cohortName}
        />
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          height: "100%",
          px: 0.25,
          py: 0.25,
          overflow: "hidden",
        }}
      >
        <StatusIcon status={status} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: view === "month" ? "10px" : "11px",
            color: "inherit",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flex: 1,
          }}
        >
          {getDisplayText() || "Appointment"}
        </Typography>
      </Box>
    </CustomTooltip>
  );
};

const durationOptions = [
  {
    label: "Day",
    value: "day",
  },
  {
    label: "Week",
    value: "week",
  },
  {
    label: "Month",
    value: "month",
  },
];

function formatTime12h(date: any) {
  return DateTime.fromJSDate(date).toFormat("hh:mm a");
}

const AppointmentsTab = ({
  toggle,
  refresh,
  setShowManualBooking,
  showManualBooking,
  viewMode,
}: {
  toggle: boolean;
  refresh: () => void;
  showManualBooking: boolean;
  setShowManualBooking: React.Dispatch<React.SetStateAction<boolean>>;
  viewMode: "slots" | "cancelled";
}) => {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((state) => state.appointment.selectedIds);
  const selectedSite = useAppSelector(
    (state) => state.appointment.selectedSite,
  );
  const selectedRoom = useAppSelector(
    (state) => state.appointment.selectedRoom,
  );
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);
  const [view, setView] = useState<any>(
    sessionStorage.getItem("reg_appt_view") || "month",
  );

  const [selectedDate, setSelectedDate] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());

  const [slots, setSlots] = useState<Slot[]>([]);

  const [showAddModal, setShowAddModal] = useState(false);
  // const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRescheduleModal, setRescheduleModal] = useState(false);

  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  useEffect(() => {
    const fetchSites = async () => {
      setLoading(true);
      try {
        const response = await http.get("/registry/sites?rooms=true");
        const data = response.data?.data;
        const siteData: Site[] = data?.map((item: any) => ({
          id: item?.id,
          name: item?.name || "-",
          timezone: item?.timezone,
          timezoneLabel: timezoneLabel(item?.timezone),
          rooms: item?.rooms || [],
        }));
        if (sessionStorage.getItem("reg_appt_site")) {
          const siteId = sessionStorage.getItem("reg_appt_site");
          const site = siteData.find((s) => s.id === siteId);
          dispatch(setSelectedSite(site || null));
          if (sessionStorage.getItem("reg_appt_room")) {
            const roomId = sessionStorage.getItem("reg_appt_room");
            const room = site?.rooms.find((r) => r.id === roomId);
            dispatch(setSelectedRoom(room || null));
          }
        }
        setSites(siteData);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [dispatch]);

  useEffect(() => {
    const fetchSites = async (roomId: string) => {
      setSlotLoading(true);
      try {
        const date = DateTime.fromJSDate(currentDate);
        const params = getCalendarParams(view, date);
        if (!params) {
          return;
        }
        setSlots([]);
        const { startDate, endDate } = params;

        let formattedSlots: Slot[] = [];
        const siteTimezone =
          selectedSite?.timezone || DateTime.local().zoneName;
        const tzLabel = timezoneLabel(siteTimezone);
        if (viewMode === "cancelled") {
          // Fetch cancelled appointments
          const response = await http.get(
            `/admin/registry/appointments/${roomId}/cancelled?startDate=${startDate
              .toUTC()
              .toISO()}&endDate=${endDate.toUTC().toISO()}`,
          );
          // Handle both direct array response and wrapped response
          const cancelledAppointments = Array.isArray(response.data)
            ? response.data
            : response.data?.data || [];

          formattedSlots = cancelledAppointments.map((apt: any) => {
            // For cancelled appointments, slot id and appointment id are the same
            const appointmentId = apt.id;
            const startInSiteTz = DateTime.fromISO(apt.scheduledFrom, {
              zone: siteTimezone,
            });
            const endInSiteTz = DateTime.fromISO(apt.scheduledTo, {
              zone: siteTimezone,
            });
            return {
              id: appointmentId,
              appointmentId: appointmentId,
              startDateTime: new Date(apt.scheduledFrom),
              endDateTime: new Date(apt.scheduledTo),
              status: 0, // Default status for cancelled
              date: DateTime.fromISO(apt.scheduledFrom).toFormat("MM/dd/yyyy"),
              time:
                DateTime.fromISO(apt.scheduledFrom).toFormat("hh:mm a") +
                " - " +
                DateTime.fromISO(apt.scheduledTo).toFormat("hh:mm a"),
              timezone: siteTimezone,
              timeZoneLabel: tzLabel,
              roomDate: startInSiteTz.toFormat("MM/dd/yyyy"),
              roomTime:
                startInSiteTz.toFormat("hh:mm a") +
                " - " +
                endInSiteTz.toFormat("hh:mm a"),
              appointment: {
                id: apt.id,
                study: apt?.study?.name,
                studyId: apt?.studyId,
                status: apt.status,
                inviters: apt.inviters?.map((inviter: any) => ({
                  id: inviter?.inviter?.id,
                  name: inviter?.inviter?.firstName
                    ? inviter?.inviter?.lastName
                      ? `${inviter?.inviter?.firstName} ${inviter?.inviter?.lastName}`
                      : inviter?.inviter?.firstName
                    : "",
                })),
                invitees: mapSlotInviteesToDetails(apt.invitees),
                notes: apt.notes,
              },
            };
          });
        } else {
          // Fetch regular slots
          const response = await http.get(
            `/admin/registry/appointment-rooms/${roomId}/slots?startDate=${startDate
              .toUTC()
              .toISO()}&endDate=${endDate.toUTC().toISO()}`,
          );

          formattedSlots = response.data.data.map((slot: any) => {
            const apt = slot?.appointment;

            const startInSiteTz = DateTime.fromISO(slot?.startDateTime, {
              zone: siteTimezone,
            });
            const endInSiteTz = DateTime.fromISO(slot?.endDateTime, {
              zone: siteTimezone,
            });
            return {
              id: slot?.id,
              appointmentId: slot?.appointmentId,
              startDateTime: new Date(slot?.startDateTime),
              endDateTime: new Date(slot?.endDateTime),
              status: slot?.status,
              date: DateTime.fromISO(slot?.startDateTime).toFormat(
                "MM/dd/yyyy",
              ),
              time:
                DateTime.fromISO(slot?.startDateTime).toFormat("hh:mm a") +
                " - " +
                DateTime.fromISO(slot?.endDateTime).toFormat("hh:mm a"),
              timezone: siteTimezone,
              timeZoneLabel: tzLabel,
              roomDate: startInSiteTz.toFormat("MM/dd/yyyy"),
              roomTime:
                startInSiteTz.toFormat("hh:mm a") +
                " - " +
                endInSiteTz.toFormat("hh:mm a"),
              appointment: apt
                ? {
                    id: apt.id,
                    study: apt?.study?.name,
                    studyId: apt?.studyId,
                    status: apt.status,
                    inviters: apt.inviters?.map((inviter: any) => ({
                      id: inviter?.inviter?.id,
                      name: inviter?.inviter?.firstName
                        ? inviter?.inviter?.lastName
                          ? `${inviter?.inviter?.firstName} ${inviter?.inviter?.lastName}`
                          : inviter?.inviter?.firstName
                        : "",
                    })),
                    invitees: mapSlotInviteesToDetails(apt.invitees),
                    notes: apt.notes,
                  }
                : undefined,
            };
          });
        }
        setSlots(formattedSlots);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setSlotLoading(false);
      }
    };
    if (selectedRoom?.id) {
      fetchSites(selectedRoom?.id);
    } else {
      setSlots([]);
    }
  }, [
    selectedRoom?.id,
    selectedSite?.timezone,
    view,
    currentDate,
    toggle,
    viewMode,
  ]);

  useEffect(() => {
    dispatch(setSelectedIds([]));
  }, [selectedRoom?.id, dispatch, view, viewMode]);

  useEffect(() => {
    const date = DateTime.fromJSDate(currentDate);
    if (view === "week") {
      const previousMonday = date
        .startOf("day")
        .minus({ days: (date.weekday - 1) % 7 });

      const upcomingSunday = previousMonday.plus({ days: 6 });

      const formattedWeekRange = `${previousMonday.toFormat(
        "MMM d, yyyy",
      )} - ${upcomingSunday.toFormat("MMM d, yyyy")}`;
      setSelectedDate(formattedWeekRange);
    } else if (view === "month") {
      setSelectedDate(date.toFormat("MMM yyyy"));
    } else if (view === "day") {
      setSelectedDate(date.toFormat("MMM d, yyyy"));
    }
  }, [setSelectedDate, currentDate, view]);

  useEffect(() => {
    let mainColor = "#872EE5";
    let subColor = "#5E13891A";
    let mainTextColor = "#111928";
    let subTextColor = "#6B7280";

    document.documentElement.style.setProperty("--primary-color", mainColor);
    document.documentElement.style.setProperty("--secondary-color", subColor);
    document.documentElement.style.setProperty("--text-color", mainTextColor);
    document.documentElement.style.setProperty(
      "--secondary-text-color",
      subTextColor,
    );
  }, []);

  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    const site = sites.find((s) => s.id === event.target.value);
    sessionStorage.setItem("reg_appt_site", site?.id || "");
    sessionStorage.setItem("reg_appt_room", "");
    dispatch(setSelectedSite(site || null));
    dispatch(setSelectedRoom(null));
  };

  const handleRoomChange = (event: SelectChangeEvent<string>) => {
    const room = selectedSite?.rooms.find((r) => r.id === event.target.value);
    dispatch(setSelectedRoom(room || null));
    sessionStorage.setItem("reg_appt_room", room?.id || "");
  };

  const viewHandler = (val: View) => {
    if (["month", "week", "day"].includes(val)) {
      setView(val);
      sessionStorage.setItem("reg_appt_view", val);
    }
  };

  const eventPropGetter = useCallback(
    (event: any) => {
      const hasAppointment = event.appointmentId !== null;
      const status = event.appointment?.status;
      const isSelected = selectedIds.includes(event.id);

      // Style selected slots
      if (isSelected) {
        return {
          style: {
            backgroundColor: "#E9D5F5",
            color: "#5E1389",
            border: "1px solid #872EE5",
          },
        };
      }

      // Style cancelled appointments in gray
      if (status === "canceled") {
        return {
          style: {
            backgroundColor: "#E5E7EB",
            color: "#6B7280",
            border: "1px solid #D1D5DB",
          },
        };
      }

      return {
        style: {
          backgroundColor: hasAppointment ? "#872EE5" : "#E5E7EB",
          color: hasAppointment ? "#FFFFFF" : "#6B7280",
          border: hasAppointment ? "1px solid #872EE5" : "1px solid #D1D5DB",
        },
      };
    },
    [selectedIds],
  );

  const drillDownHandler = (date: Date, view: View) => {
    if (date && ["month", "week", "day"].includes(view)) {
      setCurrentDate(date);
      setView(view);
      sessionStorage.setItem("reg_appt_view", view);
    }
  };

  const onSelectEvent = (event: Slot) => {
    if (event.appointmentId) {
      setSelectedSlot(event);
      setShowViewModal(true);
    } else {
      dispatch(addOrRemoveId(event.id));
    }
  };

  const navigateCalendar = (offset: number) => {
    const date = DateTime.fromJSDate(currentDate).plus({ [view]: offset });
    setCurrentDate(date.toJSDate());
  };

  const setToday = () => {
    const today = DateTime.local();
    setCurrentDate(today.toJSDate());
  };

  const handleAddAppointment = (event: Slot) => {
    setSelectedSlot(event);
    setShowAddModal(true);
  };

  // const handleDeleteSlot = (event: Slot) => {
  //   setShowDeleteModal(true);
  // };

  const handleReschedule = () => {
    setShowAddModal(false);
    setRescheduleModal(true);
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{ p: 3, bgcolor: "background.paper", mb: 2, height: "88px" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => navigateCalendar(-1)}
                size="medium"
                sx={{
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Button
                onClick={setToday}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                {view === "day"
                  ? "Today"
                  : view === "week"
                    ? "This Week"
                    : "This Month"}
              </Button>
              <IconButton
                onClick={() => navigateCalendar(1)}
                size="medium"
                sx={{
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
            <Typography fontWeight={600} fontSize={16} color="text.primary">
              {selectedDate || ""}
            </Typography>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel size="small" id="site-select-label">
                Select Location
              </InputLabel>
              <Select
                labelId="site-select-label"
                id="site-select"
                value={selectedSite?.id || ""}
                label="Select Site"
                onChange={handleSiteChange}
                disabled={loading}
                sx={{ height: "40px" }}
              >
                {sites && sites.length > 0 ? (
                  sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    <Typography color="text.secondary" fontSize={14}>
                      No locations available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              sx={{ minWidth: 200 }}
              disabled={!selectedSite}
              size="small"
            >
              <InputLabel size="small" id="room-select-label">
                Select Room
              </InputLabel>
              <Select
                labelId="room-select-label"
                id="room-select"
                value={selectedRoom?.id || ""}
                label="Select Room"
                onChange={handleRoomChange}
                sx={{ height: "40px" }}
              >
                {selectedSite?.rooms && selectedSite.rooms.length > 0 ? (
                  selectedSite.rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    <Typography color="text.secondary" fontSize={14}>
                      No rooms available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => {
              if (newView !== null) {
                viewHandler(newView);
              }
            }}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                py: 1,
                border: "1px solid",
                borderColor: "divider",
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                },
              },
            }}
          >
            {durationOptions.map((item) => (
              <ToggleButton key={item.value} value={item.value}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Paper>
      <Box sx={{ position: "relative", height: "calc(100vh - 331px)" }}>
        <Calendar
          className={
            view === "week" ? "week-view" : view === "day" ? "day-view" : ""
          }
          localizer={localizer}
          date={currentDate}
          events={slots}
          startAccessor="startDateTime"
          endAccessor="endDateTime"
          view={view}
          onView={viewHandler}
          onNavigate={() => {}}
          toolbar={false}
          eventPropGetter={eventPropGetter}
          onDrillDown={drillDownHandler}
          onSelectEvent={onSelectEvent}
          components={{
            event: (props) => (
              <CustomEvent
                {...props}
                view={view}
                onAddClick={handleAddAppointment}
                // onDeleteSlotClick={handleDeleteSlot}
              />
            ),
          }}
          scrollToTime={new Date()}
          style={{ height: "100%" }}
          formats={{
            timeGutterFormat: (date) => formatTime12h(date),
            eventTimeRangeFormat: ({ start, end }) => {
              const startTime = formatTime12h(start);
              const endTime = formatTime12h(end);
              return `${startTime} - ${endTime}`;
            },
          }}
        />
        {slotLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(2px)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {((showAddModal && selectedSlot) || showManualBooking) && (
        <AddAppointmentModal
          showModal={showAddModal || showManualBooking}
          closeModal={() => {
            setShowAddModal(false);
            setSelectedSlot(null);
            setShowManualBooking(false);
          }}
          slotData={selectedSlot}
          roomId={selectedRoom?.id || ""}
          refreshPage={refresh}
        />
      )}

      {/* {showDeleteModal && selectedSlot && (
        <DeleteSlotModal
          show={showDeleteModal}
          roomId={selectedRoom?.id || ""}
          slotData={selectedSlot}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedSlot(null);
          }}
          refresh={refresh}
        />
      )} */}

      {showViewModal && selectedSlot && (
        <ViewAppointmentModal
          showModal={showViewModal}
          closeModal={() => {
            setShowViewModal(false);
            setSelectedSlot(null);
          }}
          data={selectedSlot}
          roomId={selectedRoom?.id || ""}
          refreshPage={refresh}
          handleReschedule={handleReschedule}
        />
      )}

      {showRescheduleModal && selectedSlot && (
        <RescheduleAppointmentModal
          showModal={showRescheduleModal}
          closeModal={() => {
            setRescheduleModal(false);
            setSelectedSlot(null);
          }}
          slotData={selectedSlot}
          roomId={selectedRoom?.id || ""}
          refreshPage={refresh}
        />
      )}
    </>
  );
};

export default AppointmentsTab;
